import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LoadingIndicator,
  RadioGroup,
  Textarea,
} from '@commenda/kakashi';
import { useState } from 'react';

interface CancellationReasonStepProps {
  onClose: () => void;
  onConfirm: (reason: string) => void;
  type?: 'plan' | 'subscription';
  customReasons?: readonly string[];
  // Loading state for API calls
  isLoading?: boolean;
}

const DEFAULT_PLAN_REASONS = [
  'I want to do compliance on my own',
  'The product has technical issues',
  "I'm switching to another provider",
  "It's too expensive",
  'Other',
] as const;

const DEFAULT_SUBSCRIPTION_REASONS = [
  'I want to manage my business needs differently',
  'The subscription has technical issues',
  "I'm switching to another provider",
  "It's too expensive",
  'I no longer need these services',
  'Other',
] as const;

export const CancellationReasonStep = ({
  onClose,
  onConfirm,
  type = 'plan',
  customReasons,
  isLoading = false,
}: CancellationReasonStepProps) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');

  const reasons =
    customReasons ||
    (type === 'subscription'
      ? DEFAULT_SUBSCRIPTION_REASONS
      : DEFAULT_PLAN_REASONS);
  const modalTitle =
    type === 'subscription'
      ? "What's the main reason you want to cancel your subscription?"
      : "What's the main reason you want to cancel?";
  const keepButtonText =
    type === 'subscription' ? 'Keep the subscription' : 'Keep the plan';

  const handleConfirm = () => {
    const finalReason =
      selectedReason === 'Other' ? `Other: ${otherReason}` : selectedReason;

    if (finalReason && (selectedReason !== 'Other' || otherReason.trim())) {
      onConfirm(finalReason);
    }
  };

  const handleReasonChange = (value: string) => {
    setSelectedReason(value);
    if (value !== 'Other') {
      setOtherReason('');
    }
  };

  const canProceed =
    selectedReason !== 'Other' ? !!selectedReason : !!otherReason.trim();

  return (
    <div className="w-[676px] max-w-[676px] rounded-lg shadow-3xl">
      <DialogHeader className="flex flex-col space-y-xs items-start">
        <DialogTitle className="text-lg-bold text-text-primary">
          {modalTitle}
        </DialogTitle>
        <DialogDescription className="text-sm-regular text-text-secondary">
          Please take a minute to let us know why
        </DialogDescription>
      </DialogHeader>

      <div className="p-3xl space-y-3xl">
        <RadioGroup
          value={selectedReason}
          onValueChange={handleReasonChange}
          className="flex flex-col"
          disabled={isLoading}
        >
          {reasons.map((reason) => (
            <label
              key={reason}
              className={`flex items-center p-4 rounded-lg border border-border-secondary cursor-pointer hover:bg-gray-50 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <input
                type="radio"
                value={reason}
                checked={selectedReason === reason}
                onChange={(e) => handleReasonChange(e.target.value)}
                className="h-4 w-4 text-primary border-border-secondary focus:ring-primary"
                disabled={isLoading}
              />
              <span className="ml-3 text-sm-regular text-text-secondary">
                {reason}
              </span>
            </label>
          ))}
        </RadioGroup>

        {selectedReason === 'Other' && (
          <div className="mt-4">
            <label className="block text-sm-regular text-text-secondary mb-2">
              Reason
            </label>
            <Textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder="Enter a reason..."
              className="w-full min-h-[120px] p-3 text-sm-regular text-text-secondary"
              disabled={isLoading}
            />
          </div>
        )}
      </div>

      <DialogFooter>
        <Button
          variant="secondary"
          size="md"
          onClick={onClose}
          disabled={isLoading}
        >
          {keepButtonText}
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleConfirm}
          disabled={!canProceed || isLoading}
        >
          {isLoading ? (
            <>
              <LoadingIndicator size={16} />
              <span className="ml-2">Cancelling...</span>
            </>
          ) : (
            'Proceed to cancel'
          )}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default CancellationReasonStep;
